import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/firebaseAuth';
import { getDocumentByField } from '../../utility/firebaseHelpers';
import { Listing } from '../../components/property/property-card/PropertyCardTypes';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import CreateListingPage from '../create-listing/CreateListingPage';

interface ContinueDraftPageProps {}

const ContinueDraftPage: React.FC<ContinueDraftPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [draftListing, setDraftListing] = useState<Listing | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDraft = async () => {
      if (!id || !currentUser) {
        navigate('/add-listing');
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const listing = await getDocumentByField<Listing>(
          'listings',
          'id',
          Number(id)
        );

        if (!listing) {
          setError('Draft listing not found');
          setTimeout(() => navigate('/my-listings'), 2000);
          return;
        }

        // Verify ownership
        if (listing.propertyOwnerUid !== currentUser.uid) {
          setError("You don't have permission to access this draft");
          setTimeout(() => navigate('/my-listings'), 2000);
          return;
        }

        // Verify it's actually a draft
        if (listing.status !== 'draft') {
          setError('This listing is not a draft');
          setTimeout(() => navigate('/my-listings'), 2000);
          return;
        }

        setDraftListing(listing);
      } catch (err) {
        console.error('Error loading draft:', err);
        setError('Failed to load draft listing');
        setTimeout(() => navigate('/my-listings'), 2000);
      } finally {
        setIsLoading(false);
      }
    };

    loadDraft();
  }, [id, currentUser, navigate]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">{error}</h2>
          <p className="text-gray-600">Redirecting to My Listings...</p>
        </div>
      </div>
    );
  }

  if (!draftListing) {
    return null;
  }

  // Pass the draft listing to CreateListingPage as a prop
  return <CreateListingPage draftListing={draftListing} />;
};

export default ContinueDraftPage;
